'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, UserPlus, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Badge from '@/components/ui/Badge';
import { toast } from 'sonner';
import { shiftsService, type Shift, type StaffRole } from '@/lib/services/staffingService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DayData {
  date: string;
  dayLabel: string;
  dayName: string;
  isToday: boolean;
  shifts: Shift[];
}

const WEEK_DATES = [
  { date: '2024-07-28', dayLabel: '28', dayName: 'Mon', display: 'Jul 28' },
  { date: '2024-07-29', dayLabel: '29', dayName: 'Tue', display: 'Jul 29' },
  { date: '2024-07-30', dayLabel: '30', dayName: 'Wed', display: 'Jul 30' },
  { date: '2024-07-31', dayLabel: '31', dayName: 'Thu', display: 'Jul 31' },
  { date: '2024-08-01', dayLabel: '1', dayName: 'Fri', display: 'Aug 1' },
  { date: '2024-08-02', dayLabel: '2', dayName: 'Sat', display: 'Aug 2' },
  { date: '2024-08-03', dayLabel: '3', dayName: 'Sun', display: 'Aug 3' },
];

const availableStaff = [
  { id: 'staff-001', name: 'Fatima Hassan', role: 'lpn' as StaffRole, specialty: 'Med/Surg', credStatus: 'active', available: true },
  { id: 'staff-002', name: 'Jordan Okafor', role: 'lpn' as StaffRole, specialty: 'Pediatrics', credStatus: 'active', available: true },
  { id: 'staff-003', name: 'Tomas Herrera', role: 'rn' as StaffRole, specialty: 'Med/Surg', credStatus: 'active', available: false },
  { id: 'staff-004', name: 'Amara Diallo', role: 'cna' as StaffRole, specialty: 'General', credStatus: 'expiring', available: true },
  { id: 'staff-005', name: 'Carlos Mendez', role: 'rn' as StaffRole, specialty: 'Cardiac Care', credStatus: 'active', available: true },
];

const shiftBlockClass: Record<StaffRole, string> = {
  rn: 'shift-block-rn',
  cna: 'shift-block-cna',
  lpn: 'shift-block-lpn',
  np: 'shift-block-np',
  md: 'shift-block-rn',
};

// ─── Shift Card (shared between mobile and desktop) ───────────────────────────

function ShiftCard({ shift, onAssign }: { shift: Shift; onAssign: (s: Shift) => void }) {
  return (
    <div
      onClick={() => shift.status === 'open' ? onAssign(shift) : undefined}
      className={`rounded-md p-2.5 text-[12px] leading-tight transition-all ${
        shift.status === 'open' ?'border border-dashed border-muted-foreground/40 bg-muted/50 cursor-pointer hover:border-primary hover:bg-primary/5'
          : `${shiftBlockClass[shift.role]} cursor-default`
      } ${shift.hasConflict ? 'ring-2 ring-danger ring-offset-1' : ''}`}
    >
      {shift.status === 'assigned' ? (
        <>
          <div className="flex items-center justify-between mb-0.5">
            <span className="font-600 uppercase text-[10px] opacity-70">{shift.role}</span>
            {shift.hasConflict && <AlertTriangle size={10} className="text-danger flex-shrink-0" />}
          </div>
          <p className="font-600 truncate">{shift.staffName}</p>
          <p className="opacity-70 truncate mt-0.5">{shift.specialty}</p>
          <p className="opacity-60 mt-0.5 text-[10px] truncate">{shift.startTime}–{shift.endTime}</p>
          <p className="opacity-60 truncate text-[10px]">{shift.facility.split(' ')[0]}</p>
        </>
      ) : (
        <>
          <div className="flex items-center gap-1 text-muted-foreground mb-1">
            <UserPlus size={11} />
            <span className="font-600 uppercase text-[10px]">{shift.role} Needed</span>
          </div>
          <p className="text-muted-foreground truncate">{shift.specialty}</p>
          <p className="text-muted-foreground opacity-70 mt-0.5 text-[10px] truncate">{shift.startTime}–{shift.endTime}</p>
          <p className="text-primary font-500 mt-1 text-[10px]">Tap to assign →</p>
        </>
      )}
    </div>
  );
}

// ─── Mobile Single-Day View ───────────────────────────────────────────────────

function MobileDayView({
  calendarData,
  onAssign,
}: {
  calendarData: DayData[];
  onAssign: (s: Shift) => void;
}) {
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const goToPrev = () => setActiveDayIdx((i) => Math.max(0, i - 1));
  const goToNext = () => setActiveDayIdx((i) => Math.min(WEEK_DATES.length - 1, i + 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) goToNext();
      else goToPrev();
    }
    touchStartX.current = null;
  };

  const day = calendarData[activeDayIdx];

  return (
    <div className="card-elevated overflow-hidden">
      {/* Day navigator */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <button
          onClick={goToPrev}
          disabled={activeDayIdx === 0}
          className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors"
          aria-label="Previous day"
        >
          <ChevronLeft size={18} />
        </button>

        {/* Day pills */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
          {calendarData.map((d, idx) => (
            <button
              key={d.dayName}
              onClick={() => setActiveDayIdx(idx)}
              className={`flex flex-col items-center px-2.5 py-1 rounded-lg text-center transition-all flex-shrink-0 ${
                idx === activeDayIdx
                  ? 'bg-primary text-primary-foreground'
                  : d.isToday
                  ? 'bg-primary/10 text-primary' :'hover:bg-muted text-foreground'
              }`}
            >
              <span className="text-[9px] font-600 uppercase">{d.dayName}</span>
              <span className="text-[13px] font-700">{d.dayLabel}</span>
            </button>
          ))}
        </div>

        <button
          onClick={goToNext}
          disabled={activeDayIdx === WEEK_DATES.length - 1}
          className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors"
          aria-label="Next day"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Active day header */}
      <div className={`px-4 py-2 border-b border-border ${day.isToday ? 'bg-primary/5' : ''}`}>
        <p className="text-sm font-600 text-foreground">
          {day.dayName}, {day.date}
          {day.isToday && (
            <span className="ml-2 text-[10px] font-600 bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">Today</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">{day.shifts.length} shift{day.shifts.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Shifts for active day */}
      <div
        className="p-3 space-y-2 min-h-[200px]"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {day.shifts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
            <p className="text-sm">No shifts scheduled</p>
            <p className="text-xs mt-1">Swipe or tap a day to navigate</p>
          </div>
        ) : (
          day.shifts.map((shift) => (
            <ShiftCard key={shift.id} shift={shift} onAssign={onAssign} />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SchedulingCalendar() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Build calendar days from shifts
  const calendarData: DayData[] = WEEK_DATES.map((d) => ({
    date: d.display,
    dayLabel: d.dayLabel,
    dayName: d.dayName,
    isToday: d.date === '2024-08-03',
    shifts: shifts.filter((s) => s.shiftDate === d.date),
  }));

  const openShifts = shifts.filter((s) => s.status === 'open').length;

  const loadShifts = useCallback(async () => {
    try {
      const data = await shiftsService.getAll();
      setShifts(data);
    } catch {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShifts();
    const unsub = shiftsService.subscribe(
      (s) => setShifts((prev) => [...prev, s]),
      (s) => setShifts((prev) => prev.map((x) => (x.id === s.id ? s : x))),
      (id) => setShifts((prev) => prev.filter((x) => x.id !== id)),
    );
    return unsub;
  }, [loadShifts]);

  const handleOpenAssign = (shift: Shift) => {
    setSelectedShift(shift);
    setSelectedStaff(null);
    setAssignModalOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedStaff || !selectedShift) return;
    setAssigning(true);
    try {
      const staffMember = availableStaff.find((s) => s.id === selectedStaff);
      const updated = await shiftsService.update(selectedShift.id, {
        staffName: staffMember?.name || 'Assigned',
        status: 'assigned',
      });
      if (updated) {
        setShifts((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        toast.success(`Shift assigned to ${staffMember?.name}`, {
          description: `${selectedShift.specialty} — ${selectedShift.facility}`,
        });
      }
    } catch {
      toast.error('Failed to assign shift');
    } finally {
      setAssigning(false);
      setAssignModalOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground text-sm">Loading shifts…</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 2xl:grid-cols-4 gap-5">
      {/* ── Mobile: single-day view (hidden on md+) ── */}
      <div className="md:hidden xl:col-span-3">
        <MobileDayView calendarData={calendarData} onAssign={handleOpenAssign} />
      </div>

      {/* ── Desktop: 7-column grid (hidden below md) ── */}
      <div className="hidden md:block xl:col-span-3 2xl:col-span-3 card-elevated overflow-hidden">
        {/* Calendar header row */}
        <div className="grid grid-cols-7 border-b border-border">
          {calendarData.map((day) => (
            <div
              key={`header-${day.dayName}`}
              className={`px-2 py-3 text-center border-r border-border last:border-r-0 ${day.isToday ? 'bg-primary/5' : ''}`}
            >
              <p className="text-[10px] font-600 text-muted-foreground uppercase tracking-wider">{day.dayName}</p>
              <p className={`text-lg font-700 mt-0.5 ${day.isToday ? 'text-primary' : 'text-foreground'}`}>
                {day.dayLabel}
              </p>
              <p className="text-[10px] text-muted-foreground">{day.date}</p>
            </div>
          ))}
        </div>

        {/* Shift blocks */}
        <div className="grid grid-cols-7 min-h-[480px]">
          {calendarData.map((day) => (
            <div
              key={`col-${day.dayName}`}
              className={`border-r border-border last:border-r-0 p-2 space-y-1.5 ${day.isToday ? 'bg-primary/5' : ''}`}
            >
              {day.shifts.map((shift) => (
                <ShiftCard key={shift.id} shift={shift} onAssign={handleOpenAssign} />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Right sidebar */}
      <div className="xl:col-span-1 2xl:col-span-1 space-y-4">
        {/* Open shifts summary */}
        <div className="card-elevated p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-600 text-foreground">Open Shifts</h3>
            <span className="bg-danger/10 text-danger text-[11px] font-600 px-2 py-0.5 rounded-full">
              {openShifts} unfilled
            </span>
          </div>
          <ul className="space-y-2">
            {shifts
              .filter((s) => s.status === 'open')
              .map((s) => {
                const day = WEEK_DATES.find((d) => d.date === s.shiftDate);
                return (
                  <li
                    key={`open-${s.id}`}
                    onClick={() => handleOpenAssign(s)}
                    className="flex items-start gap-2.5 p-2.5 rounded-lg border border-dashed border-muted-foreground/30 hover:border-primary hover:bg-primary/5 cursor-pointer transition-all"
                  >
                    <Badge variant={s.role}>{s.role.toUpperCase()}</Badge>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-600 text-foreground">{s.specialty}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{s.facility}</p>
                      <p className="text-[11px] text-muted-foreground">{day?.dayName} {day?.display} · {s.startTime}</p>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>

        {/* Staff availability today */}
        <div className="card-elevated p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-600 text-foreground">Staff Available Today</h3>
            <span className="text-[11px] text-muted-foreground">Aug 3</span>
          </div>
          <ul className="space-y-2">
            {availableStaff.map((staff) => (
              <li key={staff.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[10px] font-700 flex items-center justify-center flex-shrink-0">
                  {staff.name.split(' ').map((n) => n[0]).join('')}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-600 text-foreground truncate">{staff.name}</p>
                  <p className="text-[10px] text-muted-foreground">{staff.role.toUpperCase()} · {staff.specialty}</p>
                </div>
                <span className={`text-[9px] font-600 px-1.5 py-0.5 rounded-full ${staff.available ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                  {staff.available ? 'Avail' : 'Busy'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Assign Modal */}
      <Modal open={assignModalOpen} onClose={() => setAssignModalOpen(false)} title="Assign Staff to Shift" size="sm">
        {selectedShift && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-muted/50 border border-border">
              <p className="text-sm font-600 text-foreground">{selectedShift.specialty} — {selectedShift.facility}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{selectedShift.startTime} – {selectedShift.endTime}</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Select Staff Member</p>
              {availableStaff.filter((s) => s.available).map((staff) => (
                <button
                  key={staff.id}
                  onClick={() => setSelectedStaff(staff.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                    selectedStaff === staff.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/50'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-700 flex items-center justify-center flex-shrink-0">
                    {staff.name.split(' ').map((n) => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-600 text-foreground">{staff.name}</p>
                    <p className="text-xs text-muted-foreground">{staff.role.toUpperCase()} · {staff.specialty}</p>
                  </div>
                  <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${staff.credStatus === 'active' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                    {staff.credStatus}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setAssignModalOpen(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-500 text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedStaff || assigning}
                className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {assigning && <Loader2 size={13} className="animate-spin" />}
                Assign
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}